// Copyright (c) StrangeBee 2024

import { useCallback, useEffect, useState } from 'react';
import { AttachmentType, ReportTemplateWidgetEnum, ReportTemplateWidgetImage } from 'thehive-sdk';
import { BaseWidgetFormProps } from '../FormInterface';
import { useLocalMessage } from '@Helpers/locale';
import { THForm } from '@Molecules/THForm/THForm';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { RcFile, UploadFile } from 'antd/lib/upload/interface';
import UploadSingleFile from '@Atoms/UploadSingleFile/UploadSingleFile';
import { caseReportTemplateImageUpload } from '@Helpers/download';
import useListQuery from '@Hooks/useListQuery';
import client from '@Client';
import { THInput } from '@Molecules/Inputs/THInput/THInput';

import './ImageForm.scss';

type ImageFormProps = BaseWidgetFormProps<ReportTemplateWidgetImage>;

export const ImageForm = ({ onFinish, defaultValues, reportTemplateId, onCancel }: ImageFormProps) => {
    const i = useLocalMessage();
    const [isDisabled, setIsDisabled] = useState(true);

    const [file, setFile] = useState<UploadFile>();
    const [titleLength, setTitleLength] = useState<number>(0);

    const { refetch } = useListQuery<AttachmentType[]>({
        controller: client.ReportTemplateController,
        queryParams: {
            selector: {
                head: [{ name: 'getCaseReportTemplate', idOrName: reportTemplateId }, { name: 'attachments' }],
                name: `get-case-report-templates-${reportTemplateId}`,
            },
            sort: [],
        },
        manual: true,
        fetchOnStart: false,
    });

    useEffect(() => {
        if (defaultValues?.attachmentId) {
            // get Image in order to init Uploader
            refetch().then(({ data }) => {
                const fileToEdit = data?.find((upFile) => upFile._id === defaultValues.attachmentId);
                if (fileToEdit) {
                    setFile({
                        ...fileToEdit,
                        fileName: fileToEdit.name,
                        uid: fileToEdit._id,
                    } as UploadFile);
                }
            });
        }
    }, [defaultValues, refetch]);

    const handleOnFinish = useCallback(
        async (values) => {
            try {
                if (defaultValues?.attachmentId !== file?.uid) {
                    // We upload the image on API attached to current reportTemplate
                    const attachment = await caseReportTemplateImageUpload(reportTemplateId, file as RcFile);
                    if (attachment) {
                        // Callback
                        onFinish({
                            ...defaultValues,
                            attachmentId: attachment._id,
                            _kind: ReportTemplateWidgetEnum.Image,
                            ...values,
                        });
                    }
                } else {
                    onFinish({
                        ...defaultValues,
                        ...values,
                    });
                }
            } catch (error) {}
        },
        [defaultValues, file, onFinish, reportTemplateId],
    );

    const handleImageChange = useCallback(
        (data?: UploadFile): void => {
            setFile(data);
            setIsDisabled(false);
        },
        [setFile],
    );

    return (
        <THForm<ReportTemplateWidgetImage>
            name="report-image-form"
            className="report-image-form"
            onFinish={handleOnFinish}
        >
            <THForm.Layout footer={<FormSubmitButton disabled={!file || isDisabled} onCancel={onCancel} />}>
                <THForm.Item
                    name="title"
                    label={i('common.title')}
                    rules={[
                        {
                            max: 512,
                            message: i('report-template.widgets.title.error', {
                                curr: titleLength?.toString(),
                            }),
                        },
                    ]}
                    initialValue={defaultValues?.title}
                >
                    <THInput
                        onChange={(e) => {
                            setTitleLength(e.target.value.length);
                            setIsDisabled(false);
                        }}
                    />
                </THForm.Item>
                <div className="report-image-form__container">
                    <UploadSingleFile
                        text={
                            <>
                                <div className="report-image-form__label">
                                    {i('report-template.image.form.imageUrl.label')}
                                </div>
                                <div className="report-image-form__help">
                                    {i('report-template.image.form.imageUrl.help')}
                                </div>
                            </>
                        }
                        onChange={handleImageChange}
                        value={file}
                        accept=".png,.jpeg,.jpg"
                        className="report-image-form__uploadZone"
                    />
                </div>
            </THForm.Layout>
        </THForm>
    );
};
